/**
 * Smoothly scroll the results section to just below the sticky header after a
 * filter, sort, search, or pagination change. Respects prefers-reduced-motion.
 * Where it LANDS is `#experiences-results`'s `scroll-margin-top`, set in
 * `globals.css` from the measured chrome — not a `scroll-mt-*` utility, which
 * is what this used to say and what the element used to carry. A Tailwind
 * class re-added here would lose to that rule's ID selector anyway, and appear
 * to do nothing.
 */
export function scrollToResultsTop(): void {
  if (typeof window === "undefined") return;
  const reduce = window.matchMedia?.(
    "(prefers-reduced-motion: reduce)",
  ).matches;
  const behavior: ScrollBehavior = reduce ? "auto" : "smooth";
  const el = document.getElementById("experiences-results");
  if (el) el.scrollIntoView({ behavior, block: "start" });
  else window.scrollTo({ top: 0, behavior });
}

/**
 * The same return-to-top, but only once the results have actually been
 * scrolled past.
 *
 * A filter can be applied from anywhere: the desktop sidebar is sticky and the
 * mobile sheet opens over whatever the traveller was reading, so a change made
 * near the end of a long list otherwise re-renders a new, shorter list far
 * above the viewport and leaves them staring at blank page. Pagination already
 * solves this by always scrolling; a filter cannot, because scrolling the page
 * out from under a control the traveller is still clicking is its own bug —
 * ticking three boxes in a row should not yank the panel each time.
 *
 * So the condition is whether the results top is already off the top of the
 * screen. Above it, nothing moves.
 */
const SETTLE_MS = 250;

/** When the current burst stops suppressing further jumps. */
let suppressUntil = 0;

export function scrollToResultsTopIfScrolledPast(): void {
  if (typeof window === "undefined") return;

  // LEADING EDGE, with the rest of the burst suppressed.
  //
  // A filter change is not always one deliberate act: `PriceRange` commits
  // from `onKeyUp` (`_filter-panel.client.tsx`), so a traveller nudging the
  // slider with the arrow keys fires one of these per key release, and ticking
  // several checkboxes in a row does the milder version.
  //
  // A TRAILING timer was the first attempt and it made the single click worse.
  // It moved the jump 250ms later — into the window where the traveller has
  // already started scrolling themselves — so instead of moving before their
  // finger did, it fought them. Re-reading the position at fire time did not
  // save it: they had scrolled DOWN, so the anchor was still off-screen and
  // the jump still fired.
  //
  // Jumping on the FIRST change and ignoring the rest of the burst keeps the
  // immediate feedback and still collapses the slider's key-repeat to one
  // move. It also needs no timer at all — which is what removes the other
  // half of the problem: a pending callback outliving this page, firing
  // against a route with no results anchor and yanking it to the top through
  // the fallback below, or restarting a smooth scroll that pagination had
  // already begun.
  const now = performance.now();
  if (now < suppressUntil) {
    // Still mid-burst: hold the window open so a long key-repeat cannot slip
    // a second jump in behind it.
    suppressUntil = now + SETTLE_MS;
    return;
  }
  suppressUntil = now + SETTLE_MS;

  const el = document.getElementById("experiences-results");
  // No anchor (an older tree) → fall back to the page's own scroll position.
  const scrolledPast = el
    ? el.getBoundingClientRect().top < 0
    : window.scrollY > 0;
  if (scrolledPast) scrollToResultsTop();
}
